import { Input, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { UnresolvedVendorErrorCodeResponseDto } from '@evimo/client/models';
import { useTranslation } from 'react-i18next';
import { UnresolvedVendorErrorCodeDrawer } from '~/app/components/errorManagement/UnresolvedVendorErrorCodeDrawer/UnresolvedVendorErrorCodeDrawer';
import { UnresolvedVendorErrorCodeViewDrawer } from '~/app/components/errorManagement/UnresolvedVendorErrorCodeViewDrawer/UnresolvedVendorErrorCodeViewDrawer';
import { useUnresolvedStatusOptions } from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/hooks/useUnresolvedStatusOptions';
import { useUnresolvedVendorErrorCodeColumns } from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/hooks/useUnresolvedVendorErrorCodeColumns';
import type { CreatedAtSortOrder } from '~/app/components/errorManagement/CreatedAtSortHeader/CreatedAtSortHeader';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';
import { EvimoListPanel } from '~/app/components/common/EvimoListPanel/EvimoListPanel';
import { FilterBar } from '~/app/components/common/FilterBar/FilterBar';
import type {
  SelectOption,
  UnresolvedStatusFilter,
  UnresolvedVendorErrorCodeFormValues,
} from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/types';

export type UnresolvedVendorErrorCodeTabProps = {
  rows: UnresolvedVendorErrorCodeResponseDto[];
  total: number;
  page: number;
  pageSize: number;
  searchValue: string;
  manufacturerFilter: string | undefined;
  statusFilter: UnresolvedStatusFilter;
  manufacturerOptions: SelectOption[];
  assetTypeOptions: SelectOption[];
  sortOrder: CreatedAtSortOrder;
  isLoading: boolean;
  isDrawerOpen: boolean;
  drawerRecord: UnresolvedVendorErrorCodeResponseDto | null;
  isViewDrawerOpen: boolean;
  viewingRecord: UnresolvedVendorErrorCodeResponseDto | null;
  hasConflict: boolean;
  isSubmitting: boolean;
  onSearchChange: (value: string) => void;
  onManufacturerFilterChange: (value: string | undefined) => void;
  onStatusFilterChange: (value: UnresolvedStatusFilter) => void;
  onPageChange: (page: number, pageSize: number) => void;
  onToggleSortOrder: () => void;
  onView: (record: UnresolvedVendorErrorCodeResponseDto) => void;
  onEdit: (record: UnresolvedVendorErrorCodeResponseDto) => void;
  onResolve: (record: UnresolvedVendorErrorCodeResponseDto) => void;
  onIgnore: (record: UnresolvedVendorErrorCodeResponseDto) => void;
  onUnignore: (record: UnresolvedVendorErrorCodeResponseDto) => void;
  onDrawerClose: () => void;
  onViewDrawerClose: () => void;
  onDrawerSave: (values: UnresolvedVendorErrorCodeFormValues) => Promise<void>;
  onReloadServerCopy: () => Promise<void>;
};

export function UnresolvedVendorErrorCodeTab({
  rows,
  total,
  page,
  pageSize,
  searchValue,
  manufacturerFilter,
  statusFilter,
  manufacturerOptions,
  assetTypeOptions,
  sortOrder,
  isLoading,
  isDrawerOpen,
  drawerRecord,
  isViewDrawerOpen,
  viewingRecord,
  hasConflict,
  isSubmitting,
  onSearchChange,
  onManufacturerFilterChange,
  onStatusFilterChange,
  onPageChange,
  onToggleSortOrder,
  onView,
  onEdit,
  onResolve,
  onIgnore,
  onUnignore,
  onDrawerClose,
  onViewDrawerClose,
  onDrawerSave,
  onReloadServerCopy,
}: UnresolvedVendorErrorCodeTabProps) {
  const { t } = useTranslation('errorManagement');
  const statusOptions = useUnresolvedStatusOptions();
  const columns = useUnresolvedVendorErrorCodeColumns({
    sortOrder,
    onToggleSortOrder,
    onView,
    onEdit,
    onResolve,
    onIgnore,
    onUnignore,
  });

  return (
    <>
      <EvimoListPanel>
        <FilterBar>
          <Input
            allowClear
            className="evimo-filter-search"
            prefix={<SearchOutlined />}
            placeholder={t('unresolved.searchPlaceholder')}
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('manufacturerFilter')}
            value={manufacturerFilter}
            onChange={(value) => onManufacturerFilterChange(value)}
            options={manufacturerOptions}
          />
          <Select
            allowClear
            placeholder={t('unresolved.statusFilter')}
            value={statusFilter}
            onChange={(value) => onStatusFilterChange(value)}
            options={statusOptions}
          />
        </FilterBar>

        <EvimoTable
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={rows}
          locale={{ emptyText: t('unresolved.empty') }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: onPageChange,
          }}
        />
      </EvimoListPanel>

      <UnresolvedVendorErrorCodeDrawer
        open={isDrawerOpen}
        record={drawerRecord}
        manufacturerOptions={manufacturerOptions}
        assetTypeOptions={assetTypeOptions}
        hasConflict={hasConflict}
        submitting={isSubmitting}
        onClose={onDrawerClose}
        onSave={onDrawerSave}
        onReloadServerCopy={onReloadServerCopy}
      />

      <UnresolvedVendorErrorCodeViewDrawer
        open={isViewDrawerOpen}
        record={viewingRecord}
        onClose={onViewDrawerClose}
      />
    </>
  );
}
